import { styled } from "goober";
import MarkdownToJsx from "markdown-to-jsx";
import {
  Children,
  createContext,
  createElement,
  isValidElement,
  memo,
  useContext,
  useRef,
} from "react";
import { Fragment } from "react/jsx-runtime";
import type { ChatReference } from "../hooks/useChatReference";
import { isVolumeFilePath } from "../lib/apiPaths";
import { ChatReferencePill } from "./Chat/ChatReferencePill";
import { MarkdownTable } from "./Chat/MarkdownTable";
import { ResearchCitation } from "./Chat/ResearchCitations";
import { groupResearchCitations } from "./Chat/researchCitationLinks";
import { CopyButton } from "./CopyButton";
import { highlightCode } from "./highlightCode";
import { Link } from "./Link";
import { Mermaid } from "./Mermaid";

const PreWrapper = styled("div")`
  position: relative;
  & > pre {
    position: relative;
    z-index: 0;
  }
  &:hover [data-copy-button] {
    opacity: 1;
  }
`;

const TableWrapper = styled("div")`
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
`;

function Table(props: React.TableHTMLAttributes<HTMLTableElement>) {
  return (
    <TableWrapper>
      <table {...props} />
    </TableWrapper>
  );
}

function Code(props: React.HTMLAttributes<HTMLElement>) {
  const langMatch = /(?:^|\s)lang-(\S+)/.exec(props.className ?? "");
  const lang = langMatch?.[1];
  if (!lang || typeof props.children !== "string") return <code {...props} />;
  return <code {...props}>{highlightCode(props.children, lang)}</code>;
}

function CodeBlock(props: React.HTMLAttributes<HTMLPreElement>) {
  const child = Children.toArray(props.children).find(isValidElement);
  const childProps = child?.props as
    | { className?: string; children?: unknown }
    | undefined;
  if (
    /(?:^|\s)lang-mermaid(?:\s|$)/.test(childProps?.className ?? "") &&
    typeof childProps?.children === "string"
  )
    return <Mermaid source={childProps.children} />;
  return <CodeBlockWithCopy {...props} />;
}

function CodeBlockWithCopy(props: React.HTMLAttributes<HTMLPreElement>) {
  const preRef = useRef<HTMLPreElement>(null);
  return (
    <PreWrapper>
      <pre ref={preRef} {...props} />
      <CopyButton getText={() => preRef.current?.textContent} />
    </PreWrapper>
  );
}

type Props = {
  children: string;
  citationEntryId?: string;
  internalHref?: (href: string) => string | null;
  reference?: (href: string) => ChatReference | null;
  tableControls?: boolean;
};

// Returns true when it opened the link in place, so a plain click doesn’t also open a tab.
export const LinkPreviewContext = createContext<
  ((href: string) => boolean) | null
>(null);

function Anchor({
  href,
  children,
  internalHref,
  reference,
  citationEntryId,
  ...rest
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  citationEntryId?: string;
  internalHref?: (href: string) => string | null;
  reference?: (href: string) => ChatReference | null;
}) {
  const preview = useContext(LinkPreviewContext);
  if (citationEntryId && href?.startsWith("#source-"))
    return (
      <ResearchCitation
        sourceIds={[href.slice(8)]}
        messageId={citationEntryId}
      />
    );
  const chatReference = href ? reference?.(href) : null;
  if (chatReference)
    return (
      <ChatReferencePill reference={chatReference}>
        {children}
      </ChatReferencePill>
    );
  const resolved = href ? internalHref?.(href) : null;
  if (resolved && isVolumeFilePath(resolved.replace(/\?.*/, "")))
    return (
      <a href={resolved} download {...rest}>
        {children}
      </a>
    );
  if (resolved)
    return (
      <Link to={resolved} {...rest}>
        {children}
      </Link>
    );
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={
        preview && href
          ? (event) => {
              if (
                event.button === 0 &&
                !event.metaKey &&
                !event.ctrlKey &&
                !event.shiftKey &&
                !event.altKey &&
                preview(href)
              )
                event.preventDefault();
            }
          : undefined
      }
      {...rest}
    >
      {children}
    </a>
  );
}

// Re-renders rebuild `options` and defeat markdown-to-jsx's internal compile cache.
export const Markdown = memo(function Markdown({
  children,
  internalHref,
  reference,
  tableControls,
  citationEntryId,
}: Props) {
  return (
    <MarkdownToJsx
      options={{
        createElement: citationEntryId
          ? (tag, props, ...nodes) =>
              createElement(
                tag,
                props,
                ...groupResearchCitations(nodes, citationEntryId)
              )
          : undefined,
        wrapper: Fragment,
        forceBlock: true,
        disableParsingRawHTML: true,
        overrides: {
          a: {
            component: (props: React.AnchorHTMLAttributes<HTMLAnchorElement>) =>
              Anchor({ ...props, internalHref, reference, citationEntryId }),
          },
          pre: {
            component: CodeBlock,
          },
          code: {
            component: Code,
          },
          table: {
            component: tableControls ? MarkdownTable : Table,
          },
        },
      }}
    >
      {children}
    </MarkdownToJsx>
  );
});
